/* Curated, user-controlled reviews. No requests, timers or third-party widgets. */
if (!customElements.get('dn-trustpilot-reviews')) {
  customElements.define('dn-trustpilot-reviews', class extends HTMLElement {
    connectedCallback() {
      if (this.controller) return;
      const data = this.querySelector('[data-review-data]');
      if (!data) return;
      const { reviews, labels } = JSON.parse(data.textContent);
      if (!Array.isArray(reviews) || !reviews.length) return;
      this.reviews = reviews;
      this.labels = labels;
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.nameNode = this.querySelector('[data-review-name]');
      this.textNode = this.querySelector('[data-review-text]');
      this.bodyNode = this.querySelector('[data-review-swipe]');
      this.verifiedNode = this.querySelector('[data-review-verified]');
      this.expandButton = this.querySelector('[data-review-expand]');
      this.previousButton = this.querySelector('[data-review-previous]');
      this.nextButton = this.querySelector('[data-review-next]');
      this.positionNode = this.querySelector('[data-review-position]');
      this.statusNode = this.querySelector('[data-review-status]');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.previousButton.disabled = false;
      this.nextButton.disabled = false;
      this.previousButton.addEventListener('click', () => this.showReview(this.index - 1), { signal });
      this.nextButton.addEventListener('click', () => this.showReview(this.index + 1), { signal });
      this.expandButton.addEventListener('click', () => this.toggleExpanded(), { signal });
      this.addEventListener('keydown', (event) => {
        if (!event.target.closest('[data-review-controls]')) return;
        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
        event.preventDefault();
        this.showReview(this.index + (event.key === 'ArrowRight' ? 1 : -1));
      }, { signal });
      this.bodyNode.addEventListener('pointerdown', (event) => {
        if (event.pointerType !== 'touch' || !event.isPrimary) return;
        this.touchStart = { id: event.pointerId, x: event.clientX, y: event.clientY };
        this.bodyNode.setPointerCapture(event.pointerId);
      }, { signal });
      this.bodyNode.addEventListener('pointerup', (event) => {
        const start = this.touchStart;
        this.touchStart = null;
        if (!start || start.id !== event.pointerId) return;
        const dx = event.clientX - start.x;
        const dy = event.clientY - start.y;
        if (Math.abs(dx) >= 48 && Math.abs(dx) > Math.abs(dy) * 1.5) {
          this.showReview(this.index + (dx < 0 ? 1 : -1));
        }
      }, { signal });
      this.bodyNode.addEventListener('pointercancel', () => { this.touchStart = null; }, { signal });
      this.motionQuery.addEventListener('change', () => {
        if (this.motionQuery.matches) this.cancelAnimation();
      }, { signal });
      this.showReview(0);
      this.observer = new ResizeObserver((entries) => {
        const width = entries[0].contentRect.width;
        if (width === this.lastWidth) return;
        this.lastWidth = width;
        this.cancelAnimation();
        this.updateExpansionControl();
      });
      this.observer.observe(this);
      document.fonts?.ready.then(() => {
        if (this.isConnected && !signal.aborted) this.updateExpansionControl();
      });
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.controller = null;
      this.observer?.disconnect();
      this.cancelAnimation();
      this.touchStart = null;
    }

    cancelAnimation() {
      this.heightAnimation?.cancel();
      this.heightAnimation = null;
    }

    showReview(index) {
      this.cancelAnimation();
      this.index = (index + this.reviews.length) % this.reviews.length;
      const review = this.reviews[this.index];
      this.expanded = false;
      this.dataset.expanded = 'false';
      this.dataset.long = String(review.long);
      this.nameNode.textContent = review.name;
      this.textNode.textContent = review.text;
      this.verifiedNode.hidden = !review.verified;
      this.positionNode.textContent = `${this.index + 1} / ${this.reviews.length}`;
      this.expandButton.setAttribute('aria-expanded', 'false');
      this.expandButton.textContent = this.labels.viewFull;
      this.updateExpansionControl();
      const status = this.labels.position
        .replace('__INDEX__', String(this.index + 1))
        .replace('__NAME__', review.name);
      if (this.statusNode.textContent !== status) this.statusNode.textContent = status;
    }

    updateExpansionControl() {
      if (this.expanded) return;
      const overflows = this.textNode.scrollHeight > this.textNode.clientHeight + 1;
      this.expandButton.hidden = !this.reviews[this.index].long || !overflows;
    }

    toggleExpanded() {
      const start = this.bodyNode.getBoundingClientRect().height;
      this.cancelAnimation();
      this.expanded = !this.expanded;
      this.dataset.expanded = String(this.expanded);
      this.expandButton.setAttribute('aria-expanded', String(this.expanded));
      this.expandButton.textContent = this.expanded ? this.labels.showLess : this.labels.viewFull;
      const end = this.bodyNode.getBoundingClientRect().height;
      if (!this.motionQuery.matches && this.bodyNode.animate && Math.abs(start - end) > 1) {
        this.heightAnimation = this.bodyNode.animate(
          [{ height: `${start}px` }, { height: `${end}px` }],
          { duration: 200, easing: 'ease-out' }
        );
      }
    }
  });
}
